import { PunksmarketIndexerCriteria } from '@/models/unified-market-bid';
import { PunksFilterTuple, emptyPunksFilter } from '@/services/punksmarket-v1.service';
import { PUNKSMARKET_MAX_COLOR_ID } from '@/utils/punksmarket-criteria.util';

export interface PunksmarketCriteriaFormState {
  requiredTraitIds: number[];
  forbiddenTraitIds: number[];
  anyOfTraitIds: number[];
  requiredColorIds: number[];
  forbiddenColorIds: number[];
  anyOfColorIds: number[];
  minPixelCount: number;
  maxPixelCount: number;
  minColorCount: number;
  maxColorCount: number;
}

export function emptyCriteriaFormState(): PunksmarketCriteriaFormState {
  return {
    requiredTraitIds: [],
    forbiddenTraitIds: [],
    anyOfTraitIds: [],
    requiredColorIds: [],
    forbiddenColorIds: [],
    anyOfColorIds: [],
    minPixelCount: 0,
    maxPixelCount: 0,
    minColorCount: 0,
    maxColorCount: 0,
  };
}

/** Set bit at id in a uint256-style mask (trait 0..110, color 1..221). */
export function idsToMask(ids: number[]): bigint {
  return ids.reduce((mask, id) => {
    if (!Number.isInteger(id) || id < 0) {
      return mask;
    }
    return mask | (1n << BigInt(id));
  }, 0n);
}

export function maskToIdList(mask: bigint, maxId: number): number[] {
  const ids: number[] = [];
  for (let i = 0; i <= maxId; i++) {
    if ((mask >> BigInt(i)) & 1n) {
      ids.push(i);
    }
  }
  return ids;
}

export function isCriteriaFormEmpty(state: PunksmarketCriteriaFormState): boolean {
  return (
    state.requiredTraitIds.length === 0 &&
    state.forbiddenTraitIds.length === 0 &&
    state.anyOfTraitIds.length === 0 &&
    state.requiredColorIds.length === 0 &&
    state.forbiddenColorIds.length === 0 &&
    state.anyOfColorIds.length === 0 &&
    state.minPixelCount <= 0 &&
    state.maxPixelCount <= 0 &&
    state.minColorCount <= 0 &&
    state.maxColorCount <= 0
  );
}

export function buildFilterFromForm(
  state: PunksmarketCriteriaFormState,
): PunksFilterTuple {
  if (isCriteriaFormEmpty(state)) {
    return emptyPunksFilter();
  }
  return {
    requiredTraitMask: idsToMask(state.requiredTraitIds),
    forbiddenTraitMask: idsToMask(state.forbiddenTraitIds),
    anyOfTraitMask: idsToMask(state.anyOfTraitIds),
    requiredColorMask: idsToMask(state.requiredColorIds),
    forbiddenColorMask: idsToMask(state.forbiddenColorIds),
    anyOfColorMask: idsToMask(state.anyOfColorIds),
    minPixelCount: Math.max(0, state.minPixelCount || 0),
    maxPixelCount: Math.max(0, state.maxPixelCount || 0),
    minColorCount: Math.max(0, state.minColorCount || 0),
    maxColorCount: Math.max(0, state.maxColorCount || 0),
  };
}

export function buildIndexerCriteriaFromForm(
  state: PunksmarketCriteriaFormState,
): PunksmarketIndexerCriteria {
  const filter = buildFilterFromForm(state);
  return {
    requiredTraitMask: filter.requiredTraitMask.toString(),
    forbiddenTraitMask: filter.forbiddenTraitMask.toString(),
    anyOfTraitMask: filter.anyOfTraitMask.toString(),
    requiredColorMask: filter.requiredColorMask.toString(),
    forbiddenColorMask: filter.forbiddenColorMask.toString(),
    anyOfColorMask: filter.anyOfColorMask.toString(),
    minPixelCount: filter.minPixelCount,
    maxPixelCount: filter.maxPixelCount,
    minColorCount: filter.minColorCount,
    maxColorCount: filter.maxColorCount,
  };
}

export function validateCriteriaFormState(
  state: PunksmarketCriteriaFormState,
): string | null {
  const overlap = (a: number[], b: number[]) =>
    a.some((id) => b.includes(id));
  if (overlap(state.requiredTraitIds, state.forbiddenTraitIds)) {
    return 'A trait cannot be both required and forbidden.';
  }
  if (overlap(state.requiredColorIds, state.forbiddenColorIds)) {
    return 'A color cannot be both required and forbidden.';
  }
  if (
    state.maxPixelCount > 0 &&
    state.minPixelCount > 0 &&
    state.minPixelCount > state.maxPixelCount
  ) {
    return 'Min pixel count cannot exceed max pixel count.';
  }
  if (
    state.maxColorCount > 0 &&
    state.minColorCount > 0 &&
    state.minColorCount > state.maxColorCount
  ) {
    return 'Min color count cannot exceed max color count.';
  }
  return null;
}

export function validatePlaceBidPayload(params: {
  bidScope: 'this_punk' | 'criteria';
  punkId: string | null;
  criteria: PunksmarketCriteriaFormState;
}): string | null {
  const criteriaError = validateCriteriaFormState(params.criteria);
  if (criteriaError) {
    return criteriaError;
  }
  if (params.bidScope === 'this_punk') {
    if (!params.punkId) {
      return 'Select a punk or switch to criteria-based bid.';
    }
    return null;
  }
  if (isCriteriaFormEmpty(params.criteria)) {
    return 'Add at least one trait, color, or range filter for a criteria bid.';
  }
  return null;
}

/** Decode indexer bid criteria into form state (clone bid flow). */
export function formStateFromIndexerCriteria(
  c?: PunksmarketIndexerCriteria,
): PunksmarketCriteriaFormState {
  if (!c) {
    return emptyCriteriaFormState();
  }
  const parse = (v?: string) => {
    try {
      return v && v !== '0' ? BigInt(v) : 0n;
    } catch {
      return 0n;
    }
  };
  return {
    requiredTraitIds: maskToIdList(parse(c.requiredTraitMask), 110),
    forbiddenTraitIds: maskToIdList(parse(c.forbiddenTraitMask), 110),
    anyOfTraitIds: maskToIdList(parse(c.anyOfTraitMask), 110),
    requiredColorIds: maskToIdList(parse(c.requiredColorMask), PUNKSMARKET_MAX_COLOR_ID),
    forbiddenColorIds: maskToIdList(parse(c.forbiddenColorMask), PUNKSMARKET_MAX_COLOR_ID),
    anyOfColorIds: maskToIdList(parse(c.anyOfColorMask), PUNKSMARKET_MAX_COLOR_ID),
    minPixelCount: c.minPixelCount ?? 0,
    maxPixelCount: c.maxPixelCount ?? 0,
    minColorCount: c.minColorCount ?? 0,
    maxColorCount: c.maxColorCount ?? 0,
  };
}
